import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { OrderQuery } from '@modules/order/state/order-query';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { PartialToothUpdate, TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { TeethNumberingSystem } from '@modules/teeth-diagram/models/teeth-numbering-system.enum';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { IntuitiveCopyFromSourceMenuItem } from '@modules/rx-intuitive-copy-from-tooth/models/intuitive-copy-from-source-menu-item';
import {
	filterSelectedTeethFromSources,
	IntuitiveCopyFromSourceEligibilityDeps,
	hasTreatmentInfo,
	isIntuitiveCopyFromMenuEnabled
} from '@modules/rx-intuitive-copy-from-tooth/utils/copy-from-source-teeth.util';
import { buildCopyFromSourcePreviewLine } from '@modules/rx-intuitive-teeth-orchestrator/utils/build-treatment-preview-line';
import { fromUnitType, fromUnitTypeInBridge, IntuitiveIndication } from '../models/intuitive-indication';
import { ProcedureMapCheckedValue } from '@core/procedure-helpers/models/procedure-map-composites';
import { RxIntuitiveTeethDiagramQuery } from '../state/rx-intuitive-teeth-diagram-query';
import { RxIntuitiveTeethDiagramStore } from '../state/rx-intuitive-teeth-diagram-store';
import { RxIntuitiveToothTreatmentMapper } from './rx-intuitive-tooth-treatment-mapper.service';
import { RxIntuitiveBridgeService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-bridge-service';
import { ProcedureMap } from '@shared/models/procedure-map';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { RxIntuitiveIndicationMapperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-mapper.service';
import { RxIntuitiveIndicationRulesService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-rules.service';

@Injectable()
export class RxIntuitiveCopyFromBehaviorService {
	private readonly teethNumberingSystem$ = this.teethDiagramQuery.teethNumberingSystem$;

	private readonly copyFromSourceEligibilityDeps: IntuitiveCopyFromSourceEligibilityDeps = {
		isDefaultUnitType: unitTypeId => this.indicationMapper.isDefaultUnitType(unitTypeId as UnitTypes | null | undefined)
	};

	constructor(
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly teethDiagramStore: TeethDiagramStore,
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery,
		private readonly hostPlatformService: HostPlatformService,
		private readonly indicationsService: RxIntuitiveIndicationRulesService,
		private readonly indicationMapper: RxIntuitiveIndicationMapperService,
		private readonly intuitiveDiagramQuery: RxIntuitiveTeethDiagramQuery,
		private readonly intuitiveDiagramStore: RxIntuitiveTeethDiagramStore,
		private readonly toothTreatmentMapper: RxIntuitiveToothTreatmentMapper,
		private readonly bridgeService: RxIntuitiveBridgeService
	) {}

	readonly eligibleCopyFromSourceTeeth$: Observable<Tooth[]> = combineLatest([
		this.teethDiagramQuery.teethFlat$,
		this.intuitiveDiagramQuery.selectedToothIds$,
		this.teethDiagramQuery.upperJaw$,
		this.teethDiagramQuery.lowerJaw$,
		this.orderQuery.procedureMap$,
		this.shellQuery.rxConfiguration$
	]).pipe(
		map(([teeth, selectedToothIds, upperJaw, lowerJaw, procedureMap, rxConfiguration]) => {
			if (!selectedToothIds.length) {
				return [];
			}

			const commonIndicationIds = this.getIndications(selectedToothIds, upperJaw, lowerJaw, procedureMap, rxConfiguration);

			if (!commonIndicationIds.length) {
				return [];
			}

			const sourceTeeth: Tooth[] = teeth.filter(tooth => hasTreatmentInfo(tooth, this.copyFromSourceEligibilityDeps));
			const sourceTeethByToothId = new Map(sourceTeeth.map(tooth => [tooth.ToothID as number, tooth]));

			let dropdownOptions = filterSelectedTeethFromSources(sourceTeeth, selectedToothIds);

			dropdownOptions = dropdownOptions.filter(toothOption =>
				commonIndicationIds.includes(Number(this.getCopySourceToothIndication(toothOption)))
			);

			return dropdownOptions
				.map(sourceTooth => sourceTeethByToothId.get(sourceTooth.ToothID as number))
				.filter((tooth): tooth is Tooth => tooth != null)
				.sort((leftTooth, rightTooth) => (leftTooth.ToothID ?? 0) - (rightTooth.ToothID ?? 0));
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly copyFromSourceMenuItems$: Observable<IntuitiveCopyFromSourceMenuItem[]> = combineLatest([
		this.eligibleCopyFromSourceTeeth$,
		this.teethNumberingSystem$
	]).pipe(
		map(([eligibleTeeth, teethNumberingSystem]) =>
			eligibleTeeth.map(tooth => this.toCopyFromSourceMenuRow(tooth, teethNumberingSystem))
		)
	);

	readonly isCopyFromMenuEnabled$: Observable<boolean> = combineLatest([
		this.intuitiveDiagramQuery.selectedToothIds$,
		this.eligibleCopyFromSourceTeeth$
	]).pipe(map(([selectedToothIds, eligibleSourceTeeth]) => isIntuitiveCopyFromMenuEnabled(selectedToothIds, eligibleSourceTeeth.length)));

	isCopyFromSourceTooth(tooth: Tooth): boolean {
		return hasTreatmentInfo(tooth, this.copyFromSourceEligibilityDeps);
	}

	copyTreatmentDetailsFromSourceTooth(sourceToothId: number): void {
		const teethRecord = this.teethDiagramQuery.teethRecord;
		const sourceTooth = teethRecord[sourceToothId];
		const selectedToothIds = [...this.intuitiveDiagramQuery.selectedToothIds];

		const isTryingToCopyItself = selectedToothIds.length === 1 && selectedToothIds[0] === sourceToothId;
		const isInvalidTreatmentData = !sourceTooth || !this.isCopyFromSourceTooth(sourceTooth);

		if (selectedToothIds.length === 0 || isInvalidTreatmentData || isTryingToCopyItself) {
			return;
		}

		const commonIndicationIds = this.getIndications(
			selectedToothIds,
			this.teethDiagramQuery.teeth.upperJaw,
			this.teethDiagramQuery.teeth.lowerJaw,
			this.orderQuery.procedureMap,
			this.shellQuery.rxConfiguration
		);

		if (!commonIndicationIds.includes(Number(this.getCopySourceToothIndication(sourceTooth)))) {
			return;
		}

		const propsToApply = this.extractCopyableTreatmentProps(sourceTooth) as PartialToothUpdate;
		const selectedToothIdsToUpdate = selectedToothIds.filter(selectedToothId => teethRecord[selectedToothId] != null);

		if (selectedToothIdsToUpdate.length === 0) {
			return;
		}

		this.teethDiagramStore.updateTeethByIds(selectedToothIdsToUpdate, propsToApply);
		this.intuitiveDiagramStore.markRecentlyEditedTeeth(selectedToothIds);
	}

	private getTeethByIds(toothIds: number[], teethRecord: Record<number, Tooth>): Tooth[] {
		return toothIds.map(toothId => teethRecord[toothId]).filter((tooth): tooth is Tooth => tooth != null);
	}

	private extractCopyableTreatmentProps(source: Tooth): Partial<Tooth> {
		const { ToothID: _tid, BridgeIndex: _bi, ToothInBridgeTypeID: _tib, InternalToothInBridgeTypeID: _itib, ...rest } = source;

		return rest;
	}

	private toCopyFromSourceMenuRow(tooth: Tooth, teethNumberingSystem: TeethNumberingSystem): IntuitiveCopyFromSourceMenuItem {
		const toothForTreatment = this.toothTreatmentMapper.toIntuitiveToothForTreatment(tooth, teethNumberingSystem, {}, false);

		const indicationId = toothForTreatment.indicationId as number;
		const indicationNames = IntuitiveIndication as unknown as Record<number, string>;
		const indicationBadgeClass = indicationNames[indicationId] ?? 'default';

		return {
			toothId: tooth.ToothID as number,
			toothLabel: `#${toothForTreatment.toothNumberInSystem}`,
			indicationId,
			indicationBadgeClass,
			detailLine: buildCopyFromSourcePreviewLine(toothForTreatment)
		};
	}

	private getIndications(
		selectedToothIds: number[],
		upperJaw: Tooth[],
		lowerJaw: Tooth[],
		procedureMap: ProcedureMap | null,
		rxConfiguration?: RxConfiguration | null
	) {
		const teethRecord = this.teethDiagramQuery.teethRecord;
		const selectedTeeth = this.getTeethByIds(selectedToothIds, teethRecord);
		const canCreateBridge = !!this.bridgeService.canCreateBridgeFromTeeth(selectedTeeth, { upperJaw, lowerJaw });
		const commonIndications = this.indicationsService.getCommonIndicationsForTeeth(
			selectedTeeth,
			procedureMap as ProcedureMapCheckedValue | null | undefined,
			rxConfiguration,
			this.hostPlatformService.isIteroModeling,
			canCreateBridge
		);

		return commonIndications.map(indication => Number(indication));
	}

	private getCopySourceToothIndication(tooth: Tooth): IntuitiveIndication | null {
		if (tooth.BridgeIndex != null && tooth.ToothInBridgeTypeID != null) {
			return fromUnitTypeInBridge(tooth.ToothInBridgeTypeID);
		}

		return fromUnitType(tooth.UnitTypeID);
	}
}
